import { Dialog } from '@angular/cdk/dialog';
import { inject, Injectable } from '@angular/core';
import { openLazyDialog } from '@app/shared/functions/open-lazy-dialog.function';
import {
  IInitEventDisruptionPvsTask,
  InitEventDisruptionApiService,
  REACTIVE_API_TOPICS,
  ReactiveApiBrokerService,
} from 'ngx-mt-ui-lib/api-client';
import { openConfirmDialog } from 'ngx-mt-ui-lib/components/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  from,
  map,
  of,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';

import { DisruptionActions } from './disruption.actions';
import {
  selectDisruptionHasChanges,
  selectDisruptionId,
} from './disruption.selectors';

@Injectable()
export class DisruptionPvsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly reactiveApiBroker = inject(ReactiveApiBrokerService);
  private readonly disruptionApiService = inject(InitEventDisruptionApiService);
  private readonly dialog = inject(Dialog);

  readonly openAddPvsModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.openAddPvsModal),
        switchMap(({ disruptionId, protocolStepId }) =>
          openLazyDialog(
            this.dialog,
            () =>
              import('../add-disruption-pvs-modal/add-disruption-pvs-modal.component'),
            'AddDisruptionPvsModalComponent',
            {
              autoFocus: false,
              data: {
                disruptionId,
                protocolStepId,
              },
              panelClass: 'aligned-right-dialog',
            },
          ),
        ),
      ),
    { dispatch: false },
  );

  readonly attemptRequestAddPvs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.attemptRequestAddPvs),
      withLatestFrom(this.store.select(selectDisruptionHasChanges)),
      switchMap(([{ disruptionId, body, protocolStepId }, hasChanges]) => {
        if (!hasChanges) {
          return of(
            DisruptionActions.requestAddPvs({
              disruptionId,
              body,
              protocolStepId,
            }),
          );
        }

        const ref = openConfirmDialog(this.dialog);

        return from(ref.closed).pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(
              DisruptionActions.requestAddPvs({
                disruptionId,
                body,
                protocolStepId,
              }),
            );
          }),
        );
      }),
    ),
  );

  readonly requestAddPvs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestAddPvs),
      switchMap(({ disruptionId, body, protocolStepId }) =>
        this.disruptionApiService.addPvsShift(disruptionId, body).pipe(
          map(() => DisruptionActions.requestAddPvsSuccess({ protocolStepId })),
          catchError((error) =>
            of(
              DisruptionActions.requestAddPvsError({
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly requestAddPvsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.requestAddPvsSuccess),
        withLatestFrom(this.store.select(selectDisruptionId)),
        tap(([action, id]) => {
          this.store.dispatch(DisruptionActions.requestItemInfo({ id }));
          this.store.dispatch(DisruptionActions.requestPvsTaskList());
          const { protocolStepId } = action;
          if (typeof protocolStepId === 'number') {
            this.store.dispatch(
              DisruptionActions.requestProtocolStepsComplete({
                stepId: protocolStepId,
                completedByOther: false,
              }),
            );
          }
        }),
      ),
    { dispatch: false },
  );

  readonly requestPvsTaskList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestPvsTaskList),
      withLatestFrom(this.store.select(selectDisruptionId)),
      switchMap(([_, disruptionId]) =>
        this.disruptionApiService.getTaskList(disruptionId).pipe(
          map((pvsTaskList) =>
            DisruptionActions.requestPvsTaskListSuccess({
              pvsTaskList,
            }),
          ),
          catchError((error) =>
            of(DisruptionActions.requestPvsTaskListError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly requestSetPvsTaskToCancel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestSetPvsTaskToCancel),
      withLatestFrom(this.store.select(selectDisruptionId)),
      switchMap(([{ pvsTaskId }, disruptionId]) =>
        this.disruptionApiService
          .setPvsTaskToCancel(disruptionId, pvsTaskId)
          .pipe(
            map(() => DisruptionActions.requestSetPvsTaskToCancelSuccess()),
            catchError((error) =>
              of(DisruptionActions.requestSetPvsTaskToCancelError({ error })),
            ),
          ),
      ),
    ),
  );

  readonly requestSetPvsTaskToCancelSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.requestSetPvsTaskToCancelSuccess),
        tap(() => {
          this.store.dispatch(DisruptionActions.requestPvsTaskList());
        }),
      ),
    { dispatch: false },
  );

  readonly requestSetPvsTaskToComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestSetPvsTaskToComplete),
      withLatestFrom(this.store.select(selectDisruptionId)),
      switchMap(([{ pvsTaskId }, disruptionId]) =>
        this.disruptionApiService
          .setPvsTaskToComplete(disruptionId, pvsTaskId)
          .pipe(
            map(() => DisruptionActions.requestSetPvsTaskToCompleteSuccess()),
            catchError((error) =>
              of(DisruptionActions.requestSetPvsTaskToCompleteError({ error })),
            ),
          ),
      ),
    ),
  );

  readonly requestSetPvsTaskToCompleteSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.requestSetPvsTaskToCompleteSuccess),
        tap(() => {
          this.store.dispatch(DisruptionActions.requestPvsTaskList());
        }),
      ),
    { dispatch: false },
  );

  readonly showPvsOnGisMap$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.showPvsOnGisMap),
        tap(({ pvsShiftId }) => {
          if (!pvsShiftId) {
            return;
          }

          window.open(
            `${document.location.origin}/map/city?selectPvsShiftId=${pvsShiftId}`,
            '_blank',
          );
        }),
      ),
    { dispatch: false },
  );

  readonly watchPvsTaskList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.watchPvsTaskList),
      switchMap(() =>
        this.store.select(selectDisruptionId).pipe(
          distinctUntilChanged(),
          switchMap((disruptionId) => {
            if (typeof disruptionId !== 'number' || !disruptionId) {
              return EMPTY;
            }

            return this.reactiveApiBroker
              .watchParsed<
                IInitEventDisruptionPvsTask[]
              >(REACTIVE_API_TOPICS.disruptionPvsTaskList(disruptionId))
              .pipe(
                filter(
                  (pvsTaskList): pvsTaskList is IInitEventDisruptionPvsTask[] =>
                    Array.isArray(pvsTaskList),
                ),
                map((pvsTaskList) =>
                  DisruptionActions.requestPvsTaskListSuccess({ pvsTaskList }),
                ),
              );
          }),
          takeUntil(
            this.actions$.pipe(ofType(DisruptionActions.stopWatchPvsTaskList)),
          ),
        ),
      ),
    ),
  );
}
